import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { cn } from '@/lib/utils'
import {
  Loader2,
  AlertCircle,
  Database,
  RefreshCw,
  Search,
  X,
  Settings2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useProductionModels, useProductionModelData } from '@/hooks/useProductionDatabase'
import { TruncatedCell, formatCellValue, PAGE_SIZES, SEARCH_DEBOUNCE_MS, ROW_HEIGHT, OVERSCAN } from '@/components/database/database-utils'
import { BackupsTab } from '@/components/projects/BackupsTab'
import type { Project } from '@/types/conversations'
import type { ModelInfo, ModelDataOptions } from '@/types/database'

interface LiveDatabaseSectionProps {
  project: Project
}

export function LiveDatabaseSection({ project }: LiveDatabaseSectionProps) {
  const { t } = useTranslation('conversations')
  const [selectedModel, setSelectedModel] = useState<{ appLabel: string; modelName: string } | null>(null)

  return (
    <Tabs defaultValue="data" className="flex flex-col h-full">
      <div className="flex items-center border-b px-3 flex-shrink-0">
        <TabsList className="h-9 bg-transparent p-0 gap-2">
          <TabsTrigger
            value="data"
            className="h-9 rounded-none border-b-2 border-transparent px-3 pb-2.5 pt-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            {t('live.productionDatabase.dataTab', 'Data')}
          </TabsTrigger>
          <TabsTrigger
            value="backups"
            className="h-9 rounded-none border-b-2 border-transparent px-3 pb-2.5 pt-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            {t('live.productionDatabase.backupsTab', 'Backups')}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="data" className="flex-1 min-h-0 mt-0">
        <div className="h-full">
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
              <div className="h-full border-r">
                <ProductionModelList
                  projectId={project.id}
                  selectedModel={selectedModel}
                  onModelSelect={(appLabel, modelName) => setSelectedModel({ appLabel, modelName })}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={75} minSize={50}>
              <div className="h-full">
                <ProductionDataViewer
                  projectId={project.id}
                  appLabel={selectedModel?.appLabel ?? null}
                  modelName={selectedModel?.modelName ?? null}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </TabsContent>

      <TabsContent value="backups" className="flex-1 min-h-0 mt-0 overflow-auto p-4">
        <BackupsTab projectId={project.id} />
      </TabsContent>
    </Tabs>
  )
}

// --- Model List Panel ---

interface ProductionModelListProps {
  projectId: number
  selectedModel: { appLabel: string; modelName: string } | null
  onModelSelect: (appLabel: string, modelName: string) => void
}

function ProductionModelList({ projectId, selectedModel, onModelSelect }: ProductionModelListProps) {
  const { t } = useTranslation('conversations')
  const { data, isLoading, error, refresh, isRefreshing } = useProductionModels(projectId)
  const [searchQuery, setSearchQuery] = useState('')

  const groupedModels = useMemo(() => {
    if (!data?.models) return {}
    const filtered = searchQuery
      ? data.models.filter(m =>
          m.model_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.verbose_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.app_label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : data.models
    return filtered.reduce<Record<string, ModelInfo[]>>((acc, model) => {
      const label = model.app_label.toUpperCase()
      if (!acc[label]) acc[label] = []
      acc[label].push(model)
      return acc
    }, {})
  }, [data?.models, searchQuery])

  const appLabels = Object.keys(groupedModels).sort()

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <span className="text-sm">{t('live.productionDatabase.loading')}</span>
        <span className="text-xs text-muted-foreground mt-1">{t('live.productionDatabase.loadingDescription')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <AlertCircle className="h-6 w-6 text-destructive mb-2" />
        <span className="text-sm text-destructive mb-3">{t('live.productionDatabase.loadError')}</span>
        <Button variant="outline" size="sm" onClick={() => refresh()} disabled={isRefreshing}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isRefreshing && 'animate-spin')} />
          {t('live.productionDatabase.refresh')}
        </Button>
      </div>
    )
  }

  if (!data?.models?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center text-muted-foreground">
        <Database className="h-8 w-8 mb-2 opacity-50" />
        <span className="text-sm mb-3">{t('live.productionDatabase.noModels')}</span>
        <Button variant="outline" size="sm" onClick={() => refresh()} disabled={isRefreshing}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isRefreshing && 'animate-spin')} />
          {t('live.productionDatabase.refresh')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-3 h-10 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('database.models', 'Models')}
          </span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">
            {t('live.productionDatabase.badge')}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => refresh()}
          disabled={isRefreshing}
          title={t('live.productionDatabase.refresh')}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
        </Button>
      </div>

      <div className="px-2 py-2 border-b flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t('database.searchModels', 'Search models...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 text-xs pl-7 pr-7"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {appLabels.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            {t('database.noMatches', 'No matching models')}
          </div>
        ) : (
          <div className="py-1">
            {appLabels.map((appLabel) => (
              <div key={appLabel} className="mb-2">
                <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {appLabel}
                </div>
                {groupedModels[appLabel].map((model) => {
                  const isSelected =
                    selectedModel?.appLabel === model.app_label &&
                    selectedModel?.modelName === model.model_name
                  return (
                    <button
                      key={`${model.app_label}-${model.model_name}`}
                      onClick={() => onModelSelect(model.app_label, model.model_name)}
                      className={cn(
                        'w-full px-3 py-1.5 flex items-center justify-between text-left',
                        'hover:bg-muted/50 transition-colors',
                        isSelected && 'bg-muted'
                      )}
                    >
                      <span className="text-sm truncate">
                        {model.verbose_name || model.model_name}
                      </span>
                      <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0 h-5 flex-shrink-0">
                        {model.record_count}
                      </Badge>
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Data Viewer ---

interface ProductionDataViewerProps {
  projectId: number
  appLabel: string | null
  modelName: string | null
}

function ProductionDataViewer({ projectId, appLabel, modelName }: ProductionDataViewerProps) {
  const { t } = useTranslation('conversations')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const resetState = useCallback(() => {
    setPage(1)
    setSorting([])
    setColumnVisibility({})
    setSearch('')
    setSearchInput('')
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
  }, [])

  const orderBy = useMemo(() => {
    if (sorting.length === 0) return undefined
    const { id, desc } = sorting[0]
    return desc ? `-${id}` : id
  }, [sorting])

  const options: ModelDataOptions = useMemo(() => ({
    page,
    page_size: pageSize,
    order_by: orderBy,
    search: search || undefined,
  }), [page, pageSize, orderBy, search])

  const { data, isLoading, isFetching, error } = useProductionModelData(
    projectId, appLabel, modelName, options
  )

  const prevModelRef = useRef({ appLabel, modelName })
  const isModelChange = prevModelRef.current.appLabel !== appLabel ||
                        prevModelRef.current.modelName !== modelName

  useEffect(() => {
    if (!isFetching) prevModelRef.current = { appLabel, modelName }
  }, [appLabel, modelName, isFetching])

  useEffect(() => { resetState() }, [appLabel, modelName, resetState])

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
  }, [])

  const handleSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
    setSearch(searchInput)
    setPage(1)
  }, [searchInput])

  const handleClearSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
    setSearchInput('')
    setSearch('')
    setPage(1)
  }, [])

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    const fields = data?.fields || []
    return fields.map((field) => ({
      id: field,
      accessorKey: field,
      header: ({ column }) => (
        <button
          className={cn('flex items-center gap-1 w-full text-left', 'hover:text-foreground transition-colors')}
          onClick={() => column.toggleSorting()}
        >
          <span>{field}</span>
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronsUpDown className="h-3 w-3 opacity-40" />
          )}
        </button>
      ),
      cell: ({ getValue }) => <TruncatedCell value={formatCellValue(getValue())} />,
    }))
  }, [data?.fields])

  const tableData = useMemo(() => data?.data || [], [data?.data])

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: (updater) => {
      setSorting(updater)
      setPage(1)
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  })

  const { rows } = table.getRowModel()

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  })

  const virtualRows = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0

  if (!appLabel || !modelName) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Database className="h-12 w-12 mb-3 opacity-30" />
        <span className="text-sm">{t('database.selectModel', 'Select a model to view data')}</span>
      </div>
    )
  }

  if ((isLoading && !data) || (isFetching && isModelChange)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <span className="text-sm">{t('live.productionDatabase.loading')}</span>
        <span className="text-xs text-muted-foreground mt-1">{t('live.productionDatabase.loadingDescription')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <AlertCircle className="h-6 w-6 text-destructive mb-2" />
        <span className="text-sm text-destructive">{error.message}</span>
      </div>
    )
  }

  const totalCount = data?.total_count || 0
  const totalPages = Math.ceil(totalCount / pageSize)
  const startRecord = (page - 1) * pageSize + 1
  const endRecord = Math.min(page * pageSize, totalCount)

  if (!data?.data?.length && !search) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Database className="h-12 w-12 mb-3 opacity-30" />
        <span className="text-sm">{t('database.noData', 'No records in this table')}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-3 h-10 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{modelName}</span>
          <span className="text-xs text-muted-foreground">
            ({totalCount} {t('database.records', 'records')})
          </span>
          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Input
              placeholder={t('database.searchData', 'Search data...')}
              value={searchInput}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="h-7 w-48 text-xs pr-7"
            />
            {searchInput ? (
              <Button variant="ghost" size="icon" className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7" onClick={handleClearSearch}>
                <X className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            )}
          </div>
          {table.getAllLeafColumns().length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs">{t('database.toggleColumns', 'Toggle columns')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table.getAllLeafColumns().map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    className="text-xs"
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div ref={tableContainerRef} className="flex-1 min-h-0 overflow-auto border-t">
        <table className="w-full text-sm table-fixed">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn('px-3 py-2 text-left text-xs font-medium text-muted-foreground', 'border-b whitespace-nowrap relative group')}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={cn(
                        'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                        'opacity-0 group-hover:opacity-100 hover:bg-primary/50 transition-opacity',
                        header.column.getIsResizing() && 'opacity-100 bg-primary'
                      )}
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && <tr><td style={{ height: paddingTop }} colSpan={columns.length} /></tr>}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <tr key={row.id} className="border-b hover:bg-muted/30 transition-colors" style={{ height: ROW_HEIGHT }}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 whitespace-nowrap max-w-[300px] overflow-hidden">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              )
            })}
            {paddingBottom > 0 && <tr><td style={{ height: paddingBottom }} colSpan={columns.length} /></tr>}
          </tbody>
        </table>

        {rows.length === 0 && search && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Search className="h-8 w-8 mb-2 opacity-30" />
            <span className="text-sm">{t('database.noSearchResults', 'No results found')}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            {t('database.showing', 'Showing')} {startRecord}-{endRecord} {t('database.of', 'of')} {totalCount}
          </span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1) }}>
            <SelectTrigger className="h-7 w-[70px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground px-2">
            {t('database.page', 'Page')} {page} / {totalPages || 1}
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
